// Allowed markup inside a published legal document body.
//
// Body is still required, and a URL still does not replace it. This file is
// only about how that body may be marked up: a short allowlist, so a tablet
// that is offline can still show emphasis and lists, and so a script tag
// cannot ride in a signed bundle.
//
// The console authors in a closed markdown subset (headings, bold, italic,
// lists, https links). Publish compiles that to the allowlist. A body that
// already uses the allowlist is left as HTML and only sanitised.
//
// Lives here, not in xp/ui/src/legal/documents.ts, because the API must
// refuse an unsafe body at publish and that package must not value-import
// documents.ts.

const VOID = new Set(["br"]);
const ALLOWED = new Set(["p", "br", "strong", "em", "ul", "ol", "li", "h2", "h3", "a"]);
const DROP_WITH_CONTENT = new Set(["script", "style", "iframe", "object", "embed"]);
const HTML_TAG = /<\/?[a-zA-Z][a-zA-Z0-9]*\b/;

/**
 * Rebuild `input` using only the allowlist.
 *
 * Idempotent on already-safe markup: lowercase tags, no extra attributes,
 * `a` carrying only an https href in double quotes, `br` written as `<br>`.
 * Plain text with no `<` is returned unchanged, so existing bodies stay
 * byte-for-byte what they were.
 */
export function sanitizeDocumentHtml(input: string): string {
  if (!input.includes("<")) return input;
  return rewrite(input);
}

/** True only when sanitizing would not change a character. */
export function documentBodyIsSafe(input: string): boolean {
  return sanitizeDocumentHtml(input) === input;
}

/**
 * What a signed bundle stores, and what a popup renders.
 *
 * Already-tagged HTML is only sanitised. Everything else is the closed
 * markdown subset, then the same sanitiser, so a heading typed as `##`
 * becomes `<h2>` and a pasted script tag never survives either path.
 */
export function compileDocumentBody(input: string): string {
  if (input.trim() === "") return "";
  if (HTML_TAG.test(input)) return sanitizeDocumentHtml(input);
  return sanitizeDocumentHtml(markdownToDocumentHtml(input));
}

/**
 * Closed markdown to the document allowlist.
 *
 * `#` and `##` both become `h2` (`h1` is not on the list). `###` is `h3`.
 * A single newline inside a paragraph is `<br>`; a blank line starts another
 * `<p>`. Links are `[words](https://…)` only. Nested lists, images and
 * fenced code are not read as structure — they become text.
 */
export function markdownToDocumentHtml(input: string): string {
  const lines = input.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const blocks: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === "") {
      i += 1;
      continue;
    }

    const heading = /^(#{1,3})\s+(.+)$/.exec(line);
    if (heading !== null) {
      const tag = heading[1]!.length === 3 ? "h3" : "h2";
      blocks.push(`<${tag}>${inlineMarkdown(heading[2]!)}</${tag}>`);
      i += 1;
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i]!)) {
        items.push(`<li>${inlineMarkdown(lines[i]!.replace(/^[-*]\s+/, ""))}</li>`);
        i += 1;
      }
      blocks.push(`<ul>${items.join("")}</ul>`);
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i]!)) {
        items.push(`<li>${inlineMarkdown(lines[i]!.replace(/^\d+\.\s+/, ""))}</li>`);
        i += 1;
      }
      blocks.push(`<ol>${items.join("")}</ol>`);
      continue;
    }

    const para: string[] = [];
    while (i < lines.length) {
      const next = lines[i]!;
      if (next.trim() === "") break;
      if (/^(#{1,3})\s+/.test(next)) break;
      if (/^[-*]\s+/.test(next)) break;
      if (/^\d+\.\s+/.test(next)) break;
      para.push(next);
      i += 1;
    }
    blocks.push(`<p>${para.map(inlineMarkdown).join("<br>")}</p>`);
  }
  return blocks.join("");
}

function inlineMarkdown(raw: string): string {
  const links: string[] = [];
  const withSlots = raw.replace(/\[([^\]]+)\]\((https:\/\/[^)\s]+)\)/g, (_all, label: string, href: string) => {
    const i = links.length;
    links.push(`<a href="${href}">${applyEmphasis(escapeDocumentText(label))}</a>`);
    return `\u0000L${i}\u0000`;
  });
  const emphasised = applyEmphasis(escapeDocumentText(withSlots));
  return emphasised.replace(/\u0000L(\d+)\u0000/g, (_all, index: string) => links[Number(index)] ?? "");
}

function applyEmphasis(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
}

function escapeDocumentText(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function rewrite(input: string): string {
  let out = "";
  let i = 0;
  while (i < input.length) {
    const next = input.indexOf("<", i);
    if (next === -1) {
      out += input.slice(i);
      break;
    }
    out += input.slice(i, next);
    const end = input.indexOf(">", next + 1);
    if (end === -1) {
      out += input.slice(next);
      break;
    }
    const raw = input.slice(next + 1, end).trim();
    i = end + 1;

    if (raw.startsWith("!--")) {
      const close = input.indexOf("-->", next);
      i = close === -1 ? input.length : close + 3;
      continue;
    }

    const closing = raw.startsWith("/");
    const body = closing ? raw.slice(1).trim() : raw.replace(/\/$/, "").trim();
    const nameMatch = /^([a-zA-Z][a-zA-Z0-9]*)/.exec(body);
    if (nameMatch === null) continue;
    const name = nameMatch[1]!.toLowerCase();

    if (DROP_WITH_CONTENT.has(name)) {
      if (!closing) {
        const closer = new RegExp(`</${name}\\s*>`, "i");
        const rest = input.slice(i);
        const found = closer.exec(rest);
        i = found === null ? input.length : i + found.index + found[0].length;
      }
      continue;
    }

    if (!ALLOWED.has(name)) continue;

    if (closing) {
      if (!VOID.has(name)) out += `</${name}>`;
      continue;
    }

    if (VOID.has(name)) {
      out += `<${name}>`;
      continue;
    }

    if (name === "a") {
      const href = readHref(body.slice(name.length));
      out += href === null ? "<a>" : `<a href="${href}">`;
      continue;
    }

    out += `<${name}>`;
  }
  return out;
}

function readHref(attrs: string): string | null {
  const match = /\bhref\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrs);
  if (match === null) return null;
  const value = match[2] ?? match[3] ?? match[4] ?? "";
  if (!/^https:\/\//.test(value)) return null;
  if (/[\u0000-\u001f<>"]/.test(value)) return null;
  return value;
}
